import { useState } from 'react';
import { InputNumber, SelectAngle, SliderInput } from '@meitu/saas-ui';
import { translateAngleToXY } from '@meitu/saas-editor-core/es/utils/math-kit';
import { IImageEffectShadow } from '@meitu/saas-editor-core/es/version-types';
import { ColorGroup } from '@meitu/xdesign-widgets';
import LabelAction from '@/components/label-action';
import { useLocaleStore } from '@/i18n';
import { getColorAlpha, transferColorOpacity, transferXYToAngle, transferXYToDistance } from '../../utils';

const BaseShadowItem: React.FC<{
    shadow: IImageEffectShadow;
    updateData: (data: { key: string; value: any }[], pushHistory?: boolean, stopUpdate?: boolean) => void;
}> = ({ shadow, updateData }) => {
    const { $t } = useLocaleStore();

    const angle = transferXYToAngle((shadow?.x || 0) * 1000, (shadow?.y || 0) * 1000);
    const distance = transferXYToDistance((shadow?.x || 0) * 1000, (shadow?.y || 0) * 1000);
    const [lastAngle, setLastAngle] = useState(0);
    const colorChange = (color: string, isEnd = false) => {
        updateData([{ key: 'color', value: color }], isEnd, isEnd);
    };

    const alphaChange = (alpha: number, isEnd = false) => {
        updateData(
            [
                {
                    key: 'color',
                    value: transferColorOpacity(shadow?.color || '#000000', alpha),
                },
            ],
            isEnd,
            isEnd,
        );
    };

    const blurChange = (blur: number, isEnd = false) => {
        updateData([{ key: 'blur', value: blur }], isEnd, isEnd);
    };

    const distanceChange = (newDistance: number, oldDistance: number, isEnd = false) => {
        const { x, y } = translateAngleToXY(oldDistance === 0 && angle === 0 ? lastAngle : angle, newDistance);
        if (newDistance === 0) {
            setLastAngle(angle);
        }
        updateData(
            [
                { key: 'x', value: x / 1000 },
                { key: 'y', value: y / 1000 },
            ],
            isEnd,
            isEnd,
        );
    };

    const angleChange = (newAngle: number, isEnd = false) => {
        const { x, y } = translateAngleToXY(newAngle, distance);
        setLastAngle(newAngle);
        updateData(
            [
                { key: 'x', value: x / 1000 },
                { key: 'y', value: y / 1000 },
            ],
            isEnd,
            isEnd,
        );
    };

    return (
        <>
            <LabelAction
                title={$t((v) => v.imageEffectPanel.imageShadowItem.color)}
                align
                style={{ marginBottom: '8px' }}
            >
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        width: '100%',
                    }}
                >
                    <ColorGroup
                        popupTriggerSize={{
                            width: 58,
                            height: 26,
                        }}
                        placement="topLeft"
                        popupOverlayClassName="c-markup-panel__color-picker"
                        color={shadow?.color || '#000000'}
                        setColor={(val) => {
                            colorChange(val);
                        }}
                        setColorAfter={(val, _, { isBrandColor }) => {
                            colorChange(val, true);
                            if (isBrandColor) {
                                window._stat_track?.('hb_layers_func_use', {
                                    func_id: '9_107',
                                });
                            }
                        }}
                        showIcon={false}
                        onToggleBizTab={(bizTab) => {
                            if (bizTab === 'team') {
                                window._stat_track?.('hb_layers_func_click', {
                                    func_id: '9_107',
                                });
                            }
                        }}
                    />
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                        }}
                    >
                        <SelectAngle
                            angle={distance === 0 ? lastAngle + 180 : angle + 180}
                            showValue={false}
                            onChange={(val) => {
                                angleChange(val - 180);
                            }}
                            onAfterChange={(val) => {
                                angleChange(val - 180, true);
                            }}
                        ></SelectAngle>
                        <InputNumber
                            value={distance === 0 ? lastAngle : angle}
                            precision={1}
                            min={-180}
                            max={180}
                            style={{
                                width: '58px',
                                height: '26px',
                                marginLeft: '8px',
                            }}
                            showStepBtn
                            onChange={(val) => {
                                angleChange(val || 0);
                            }}
                        />
                    </div>
                </div>
            </LabelAction>

            <LabelAction title={$t((v) => v.imageEffectPanel.imageShadowItem.blur)} align>
                <div
                    style={{
                        width: '100%',
                    }}
                >
                    <SliderInput
                        min={0}
                        max={100}
                        value={shadow?.blur ? shadow.blur * 100 : 0}
                        precision={1}
                        showStepBtn
                        type="horizontal"
                        onChange={(val: number) => {
                            blurChange(val / 100);
                        }}
                        onAfterChange={(val: number) => {
                            blurChange(val / 100, true);
                        }}
                        onClick={() => {}}
                    />
                </div>
            </LabelAction>

            <LabelAction title={$t((v) => v.imageEffectPanel.imageShadowItem.dis)} align>
                <div
                    style={{
                        width: '100%',
                    }}
                >
                    <SliderInput
                        min={0}
                        max={100}
                        value={distance}
                        precision={1}
                        showStepBtn
                        type="horizontal"
                        onChange={(val: number) => {
                            distanceChange(val, distance);
                        }}
                        onAfterChange={(val: number) => {
                            distanceChange(val, distance, true);
                        }}
                        onClick={() => {}}
                    />
                </div>
            </LabelAction>

            <LabelAction title={$t((v) => v.imageEffectPanel.imageShadowItem.rotate)} align>
                <div
                    style={{
                        width: '100%',
                    }}
                >
                    <SliderInput
                        min={-15}
                        max={15}
                        value={shadow?.rotate || 0}
                        precision={1}
                        showStepBtn
                        type="horizontal"
                        onChange={(val: number) => {
                            updateData([{ key: 'rotate', value: val }]);
                        }}
                        onAfterChange={(val: number) => {
                            updateData([{ key: 'rotate', value: val }], true);
                        }}
                    />
                </div>
            </LabelAction>

            <LabelAction title={$t((v) => v.imageEffectPanel.imageShadowItem.opacity)} align>
                <div
                    style={{
                        width: '100%',
                    }}
                >
                    <SliderInput
                        min={0}
                        max={100}
                        value={getColorAlpha(shadow?.color || '#000000')}
                        precision={1}
                        showStepBtn
                        type="horizontal"
                        onChange={(val: number) => {
                            alphaChange(val);
                        }}
                        onAfterChange={(val: number) => {
                            alphaChange(val, true);
                        }}
                        onClick={() => {}}
                    />
                </div>
            </LabelAction>
        </>
    );
};

export default BaseShadowItem;
